import { useEffect, useState, type FormEvent } from 'react';
import { useParams, useSearchParams } from 'react-router';
import { useMutation, useQuery } from '@tanstack/react-query';
import { BILLING_METHOD_LABELS, EXTRA_USER_GROSS_MONTH_CENTS, formatCents, INCLUDED_USERS, PLAN_LABELS, PLANS, type PlaceOrderResult, type Plan, type ReactivationInfo } from '@kontor/shared';
import { api, ApiError } from '../api';
import { WEBSITE } from '../links';
import './billing.css';

const day = new Intl.DateTimeFormat('de-DE', { dateStyle: 'long', timeZone: 'Europe/Berlin' });

const METHOD_TEXT: Record<string, string> = {
  stripe: 'Sie geben Ihre Zahlungsdaten bei unserem Zahlungsdienstleister Stripe ein. Der Betrag wird einmal im Jahr eingezogen.',
  paypal: 'Sie bestätigen das Abonnement bei PayPal. Der Betrag wird einmal im Jahr eingezogen.',
};

const errorText = (e: unknown) => (e instanceof ApiError ? (e.status === 422 ? (Object.values(e.fields)[0] ?? e.message) : e.message) : 'Die Bestellung hat nicht geklappt.');

/**
 * Öffentliche Seite hinter dem Link aus der Mail „Testphase zu Ende“. Der Zugang ist gesperrt, der Kunde kann sich nicht anmelden. Das Formular ist mit den Angaben des Tests vorausgefüllt,
 * es fehlen nur Paket und Zahlung. Nach der bestätigten Zahlung wird der Zugang frei, alle Daten bleiben.
 */
export function ReactivationPage() {
  const { token = '' } = useParams();
  const [params] = useSearchParams();
  const info = useQuery({ queryKey: ['reactivation', token], queryFn: async () => (await api<{ data: ReactivationInfo }>('GET', `/shop/reactivate/${token}`)).data, retry: false });
  const wanted = params.get('paket');
  const [plan, setPlan] = useState<Plan>((PLANS as readonly string[]).includes(wanted ?? '') ? (wanted as Plan) : 'standard');
  const [method, setMethod] = useState<'stripe' | 'paypal'>('stripe');
  const [billing, setBilling] = useState({ company: '', name: '', street: '', postalCode: '', city: '', vatId: '', email: '' });
  const [terms, setTerms] = useState(false);
  const [avv, setAvv] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!info.data) return;
    setBilling(info.data.billing);
    const first = info.data.methods.find((m) => m.enabled);
    if (first) setMethod(first.key as 'stripe' | 'paypal');
  }, [info.data]);

  const place = useMutation({
    mutationFn: async () => (await api<{ data: PlaceOrderResult }>('POST', `/shop/reactivate/${token}/orders`, { plan, paymentMethod: method, billing: { ...billing, country: 'DE', vatId: billing.vatId || undefined }, acceptTerms: terms, acceptAvv: avv })).data,
    onSuccess: (result) => {
      if (result.redirectUrl) window.location.href = result.redirectUrl;
    },
    onError: (e) => setError(errorText(e)),
  });

  if (info.isLoading) return <div className="center-screen"><p className="muted">Wird geladen …</p></div>;
  const data = info.data;
  if (!data) {
    return (
      <div className="center-screen">
        <div className="card narrow" role="alert">
          <h1>Dieser Link ist nicht mehr gültig</h1>
          <p>{info.error instanceof ApiError ? info.error.message : 'Die Seite konnte nicht geladen werden.'}</p>
        </div>
      </div>
    );
  }
  const offer = data.plans.find((p) => p.plan === plan)!;
  const set = (key: keyof typeof billing) => (e: { target: { value: string } }) => setBilling((b) => ({ ...b, [key]: e.target.value }));
  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    place.mutate();
  };

  return (
    <div className="center-screen">
      <div style={{ width: '100%', maxWidth: 760 }}>
        <h1>Paket bestellen</h1>
        <p className="muted">
          Der Test von {data.companyName} ist abgelaufen, der Zugang ist gesperrt. Mit der Bestellung wird er wieder frei, und alle Daten bleiben erhalten. Bestellen Sie bis {day.format(new Date(data.deleteAt))}, danach löschen wir die Daten des Tests.
          Das Paket wird für zwölf Monate im Voraus abgerechnet und lässt sich jederzeit zum Ende des Abrechnungszeitraums kündigen.
        </p>
        {params.get('abgebrochen') && <div className="alert alert--warn" role="status">Die Zahlung wurde abgebrochen. Es wurde nichts abgebucht, Sie können es noch einmal versuchen.</div>}
        {error && <div className="alert alert--error" role="alert">{error}</div>}

        <form onSubmit={submit} noValidate>
          <h2>Paket</h2>
          <div className="plan-grid" role="radiogroup" aria-label="Paket">
            {data.plans.map((p) => (
              <label key={p.plan} className={`plan-card card${plan === p.plan ? ' plan-card--on' : ''}`}>
                <input type="radio" name="plan" value={p.plan} checked={plan === p.plan} onChange={() => setPlan(p.plan)} />
                <strong>{PLAN_LABELS[p.plan]}</strong>
                <span className="plan-card__price">{formatCents(p.grossMonthCents)} <small>brutto je Benutzer und Monat</small></span>
                <span className="muted small">{formatCents(p.grossCents)} für zwölf Monate im Voraus</span>
              </label>
            ))}
          </div>
          <p className="muted small">Alle Preise sind Bruttopreise inklusive Umsatzsteuer. In jedem Paket ist {INCLUDED_USERS} Benutzer enthalten, jeder weitere kostet {formatCents(EXTRA_USER_GROSS_MONTH_CENTS)} im Monat. Ein kleineres Paket als das im Test schaltet Module ab, die Daten bleiben dabei gespeichert.</p>

          <h2>Rechnungsanschrift</h2>
          <div className="card">
            <p className="muted small">Wir haben die Angaben aus Ihrem Test eingetragen. Bitte prüfen Sie sie und ergänzen Sie, was fehlt.</p>
            <div className="form-grid">
              <label className="field"><span>Firma <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.company} onChange={set('company')} autoComplete="organization" required /></label>
              <label className="field"><span>Ansprechpartner <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.name} onChange={set('name')} autoComplete="name" required /></label>
              <label className="field"><span>Straße und Hausnummer <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.street} onChange={set('street')} autoComplete="street-address" required /></label>
              <label className="field"><span>Postleitzahl <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.postalCode} onChange={set('postalCode')} autoComplete="postal-code" required /></label>
              <label className="field"><span>Ort <abbr title="Pflichtfeld">*</abbr></span><input className="input" value={billing.city} onChange={set('city')} autoComplete="address-level2" required /></label>
              <label className="field"><span>Umsatzsteuer-ID (freiwillig)</span><input className="input" value={billing.vatId} onChange={set('vatId')} placeholder="DE123456789" /></label>
              <label className="field field--wide"><span>E-Mail-Adresse für die Rechnung <abbr title="Pflichtfeld">*</abbr></span><input className="input" type="email" value={billing.email} onChange={set('email')} autoComplete="email" required /></label>
            </div>
          </div>

          <h2>Zahlungsart</h2>
          <div className="card">
            {data.methods.map((m) => (
              <label key={m.key} className={`check${m.enabled ? '' : ' check--off'}`}>
                <input type="radio" name="method" value={m.key} checked={method === m.key} disabled={!m.enabled} onChange={() => setMethod(m.key as 'stripe' | 'paypal')} />
                <span>
                  <strong>{BILLING_METHOD_LABELS[m.key]}</strong>
                  <span className="muted small billing-block">{m.enabled ? METHOD_TEXT[m.key] : 'Diese Zahlungsart ist noch nicht verfügbar.'}</span>
                </span>
              </label>
            ))}
            <p className="muted small">Eine Rechnung zum Überweisen gibt es hier nicht, weil der Zugang erst nach bestätigter Zahlung frei wird. Wollen Sie per Überweisung zahlen, schreiben Sie an kontakt@wirkia.de.</p>
          </div>

          <div className="card billing-summary">
            <p><strong>{PLAN_LABELS[plan]}</strong>: {formatCents(offer.grossMonthCents)} brutto je Benutzer und Monat. Die erste Abrechnung gilt ab heute für zwölf Monate: {formatCents(offer.grossCents)} brutto.</p>
            <label className="check">
              <input type="checkbox" checked={terms} onChange={(e) => setTerms(e.target.checked)} />
              <span>Ich habe die <a href={`${WEBSITE}/agb/`} target="_blank" rel="noreferrer">Allgemeinen Geschäftsbedingungen</a> gelesen und akzeptiere sie. <abbr title="Pflichtfeld">*</abbr></span>
            </label>
            <label className="check">
              <input type="checkbox" checked={avv} onChange={(e) => setAvv(e.target.checked)} />
              <span>Ich schließe den <a href={`${WEBSITE}/avv/`} target="_blank" rel="noreferrer">Vertrag zur Auftragsverarbeitung</a> ab. <abbr title="Pflichtfeld">*</abbr></span>
            </label>
            <div className="form-actions">
              <button className="btn btn--primary" type="submit" disabled={place.isPending}>{place.isPending ? 'Wird bestellt …' : 'Weiter zur Zahlung'}</button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
}
